import { useCallback, useEffect, useRef, useState } from 'react';
import {
  initPopstateDispatcher,
  registerViewHandler,
  unregisterViewHandler,
} from './popstateDispatcher';

interface ViewHistoryState<T extends string> {
  navType: 'view';
  view: T;
}

export interface UseViewBackStackResult<T extends string> {
  view: T;
  /** User-driven navigation — pushes a history entry, undoable with back. */
  setView: (next: T) => void;
  /** Non-user-driven correction (e.g. restoring a saved view on auth load) —
   * replaces the current history entry instead of pushing a new one. */
  replaceView: (next: T) => void;
}

/**
 * Wraps a `view` state value so user-driven changes are undoable with the
 * browser back button, mirroring standard SPA router behavior. Also keeps
 * `document.title` in sync with the current view, via an optional
 * `getTitle` callback, covering both the click-driven path and the
 * back/forward path with a single effect keyed on `view`.
 */
export function useViewBackStack<T extends string>(
  initialView: T | (() => T),
  getTitle?: (view: T) => string
): UseViewBackStackResult<T> {
  const [view, setViewState] = useState<T>(initialView);
  const getTitleRef = useRef(getTitle);
  getTitleRef.current = getTitle;
  const viewRef = useRef(view);
  viewRef.current = view;

  useEffect(() => {
    window.history.replaceState(
      { navType: 'view', view } as ViewHistoryState<T>,
      ''
    );
    // Intentionally only on mount — captures the initial `view` resolved by
    // useState above, regardless of whether `initialView` was a value or a
    // lazy initializer function.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (getTitleRef.current) {
      document.title = getTitleRef.current(view);
    }
  }, [view]);

  useEffect(() => {
    initPopstateDispatcher();
    function handlePop(state: unknown): void {
      const viewState = state as ViewHistoryState<T> | null;
      if (viewState && viewState.navType === 'view') {
        setViewState(viewState.view);
      }
    }
    registerViewHandler(handlePop as (state: unknown) => void);
    return () => unregisterViewHandler(handlePop as (state: unknown) => void);
  }, []);

  const setView = useCallback((next: T) => {
    if (next === viewRef.current) {
      // Already on this view (e.g. clicking the active tab again) — pushing
      // here would create a no-op back step.
      return;
    }
    window.history.pushState(
      { navType: 'view', view: next } as ViewHistoryState<T>,
      ''
    );
    setViewState(next);
  }, []);

  const replaceView = useCallback((next: T) => {
    if (next === viewRef.current) {
      return;
    }
    window.history.replaceState(
      { navType: 'view', view: next } as ViewHistoryState<T>,
      ''
    );
    setViewState(next);
  }, []);

  return { view, setView, replaceView };
}
